// combatpro/dice/diceEngine.js
//
// Combat PRO - Dice Engine (Stage 36: pure dice mathematics).
//
// Pure, UI-independent dice mathematics extracted from the dice runtime in
// battletracker.html (the read-only master spec). This module knows nothing
// about React, state, components, history, display, or context. It performs
// only deterministic rolls and formula parsing and returns plain result
// objects. It is safe to reuse from later runtime stages.
//
// Source mapping (battletracker.html):
//   - rollDie(sides)              new primitive: the inner
//                                 Math.floor(Math.random() * sides) + 1 used by
//                                 rollDice in the source.
//   - rollDice(count, sides, mod) from rollDice(sides, count, modifier, label)
//                                 at source line 4856. Argument order is
//                                 normalised to (count, sides, modifier) and the
//                                 label argument is dropped; the returned shape
//                                 keeps { rolls, total, formula }. The formula
//                                 string is produced exactly as the source did.
//   - rollFormula(expression)     from vi_rollFormula(formula) at source line
//                                 4286: parses "NdM", "NdM+K", "NdM-K" and
//                                 delegates to rollDice. On no match it returns
//                                 { total: 0, formula } exactly as the source.
//   - rollD20(modifier, advantage, disadvantage, label)
//                                 from rollD20 at source line 4868. Composes
//                                 rollDie(20). Returns the source's exact result
//                                 shape (adv/dis: rolls[2] + chosenRoll; normal:
//                                 rolls[1], no chosenRoll), with isCrit/isFail on
//                                 the chosen/raw face and the "1d20+N (ADV/DIS)"
//                                 formula.
//   - rollDamage(diceString, label)
//                                 from rollDamage at source line 4901. Parses
//                                 "NdM+K", delegates to rollDice (arg order mapped
//                                 to the migrated (count, sides, modifier) and the
//                                 label re-attached), and reproduces the source's
//                                 no-match number / zero paths. Returns
//                                 { rolls, total, formula, label }.
//
// History creation is NOT performed here (engine boundary): DiceView builds the
// history entry from the engine result. No createDiceHistoryEntry, no setState,
// no history, no display.
//
// FORBIDDEN here and absent: localStorage, API, fetch, websocket, AI, context
// imports, React imports, DOM access, document.getElementById, window globals,
// side effects. No optional chaining. No em dash.

// Roll a single die with the given number of sides. Matches the source's
// Math.floor(Math.random() * sides) + 1.
export function rollDie(sides, drawFace) {
  // Trusted headless callers inject recorded faces without replacing the dice
  // mathematics or changing browser callers' existing source of randomness.
  if (drawFace !== undefined) {
    if (typeof drawFace !== 'function') { throw new TypeError('drawFace must be a function'); }
    var face = drawFace(sides);
    if (!Number.isInteger(face) || face < 1 || face > sides) {
      throw new RangeError('Injected die face is outside the die');
    }
    return face;
  }
  return Math.floor(Math.random() * sides) + 1;
}

// Roll count dice of the given sides with an optional flat modifier. Mirrors the
// source rollDice: accumulates individual rolls, adds the modifier to the total,
// and builds the same formula string. Returns { rolls, total, formula }.
export function rollDice(count, sides, modifier, drawFace) {
  var rollCount = count;
  var mod = modifier;
  if (rollCount === undefined) { rollCount = 1; }
  if (mod === undefined) { mod = 0; }

  var rolls = [];
  var total = mod;
  for (var i = 0; i < rollCount; i++) {
    var roll = rollDie(sides, drawFace);
    rolls.push(roll);
    total += roll;
  }

  var formula = rollCount + 'd' + sides + (mod >= 0 ? '+' : '') + (mod !== 0 ? mod : '');
  return { rolls: rolls, total: total, formula: formula };
}

// Parse and roll a simple dice expression such as "8d6", "3d8+5", "1d4+1".
// Mirrors vi_rollFormula: on a non-matching expression it returns
// { total: 0, formula } without throwing.
export function rollFormula(expression, drawFace) {
  try {
    var match = expression.replace(/\s/g, '').match(/^(\d+)d(\d+)([+-]\d+)?/i);
    if (!match) { return { total: 0, formula: expression }; }
    var count = parseInt(match[1], 10);
    var sides = parseInt(match[2], 10);
    var mod = match[3] ? parseInt(match[3], 10) : 0;
    return rollDice(count, sides, mod, drawFace);
  } catch (e) {
    if (drawFace !== undefined) { throw e; }
    return { total: 0, formula: expression };
  }
}

// Roll a d20 with an optional modifier, supporting advantage / disadvantage.
// Faithful port of the legacy rollD20 (battletracker.html 4868-4898). Composes
// the existing rollDie(20) primitive for each face rather than inlining
// Math.random. Returned object shape matches the source EXACTLY:
//   - advantage/disadvantage branch: rolls two d20, chooses max (advantage) or
//     min (disadvantage), and returns { rolls:[r1,r2], sides:20, chosenRoll,
//     total, isCrit, isFail, formula, label }. isCrit/isFail test the CHOSEN
//     roll (chosenRoll === 20 / === 1). formula appends " (ADV)" or " (DIS)".
//   - normal branch: rolls one d20 and returns { rolls:[roll], sides:20, total,
//     isCrit, isFail, formula, label } - NOTE no chosenRoll field, matching the
//     source. isCrit/isFail test the single roll.
// total = chosen/raw roll + modifier. formula is "1d20" + sign + modifier
// (+ adv type in the adv/dis branch). Defaults mirror the source signature
// (modifier 0, advantage false, disadvantage false, label "").
export function rollD20(modifier, advantage, disadvantage, label, drawFace) {
  var mod = (modifier === undefined) ? 0 : modifier;
  var adv = (advantage === undefined) ? false : advantage;
  var dis = (disadvantage === undefined) ? false : disadvantage;
  var lbl = (label === undefined) ? "" : label;

  if (adv || dis) {
    var roll1 = rollDie(20, drawFace);
    var roll2 = rollDie(20, drawFace);
    var chosenRoll = adv ? Math.max(roll1, roll2) : Math.min(roll1, roll2);
    var totalA = chosenRoll + mod;
    var advType = adv ? " (ADV)" : " (DIS)";
    return {
      rolls: [roll1, roll2],
      sides: 20,
      chosenRoll: chosenRoll,
      total: totalA,
      isCrit: chosenRoll === 20,
      isFail: chosenRoll === 1,
      formula: "1d20" + (mod >= 0 ? "+" : "") + mod + advType,
      label: lbl
    };
  }

  var roll = rollDie(20, drawFace);
  var totalN = roll + mod;
  return {
    rolls: [roll],
    sides: 20,
    total: totalN,
    isCrit: roll === 20,
    isFail: roll === 1,
    formula: "1d20" + (mod >= 0 ? "+" : "") + mod,
    label: lbl
  };
}

// Parse and roll a damage dice string such as "2d6+3". Faithful port of the
// legacy rollDamage (battletracker.html 4901-4918). Composes the existing
// rollDice primitive for the matched path rather than re-implementing dice
// rolling. Behaviour matches the source EXACTLY:
//   - trims the input, then matches /(\d+)d(\d+)([+\-]\d+)?/i.
//   - no match: tries a plain number; on a valid number returns
//     { rolls:[], total:num, formula: diceString, label }; otherwise
//     { rolls:[], total:0, formula: diceString, label }.
//   - match: delegates to rollDice. The legacy call was
//     rollDice(sides, count, modifier, label); the migrated rollDice signature
//     is (count, sides, modifier) and drops the label, so the arguments are
//     mapped to (count, sides, modifier) and the label is re-attached to the
//     returned object here, preserving the legacy { rolls, total, formula, label }
//     shape.
export function rollDamage(diceString, label, drawFace) {
  var lbl = (label === undefined) ? "" : label;
  var str = diceString.trim();
  var match = str.match(/(\d+)d(\d+)([+\-]\d+)?/i);

  if (!match) {
    var num = parseInt(str, 10);
    if (!isNaN(num)) {
      return { rolls: [], total: num, formula: diceString, label: lbl };
    }
    return { rolls: [], total: 0, formula: diceString, label: lbl };
  }

  var count = parseInt(match[1], 10);
  var sides = parseInt(match[2], 10);
  var modifier = match[3] ? parseInt(match[3], 10) : 0;

  var result = rollDice(count, sides, modifier, drawFace);
  return { rolls: result.rolls, total: result.total, formula: result.formula, label: lbl };
}
